import { useQuery } from "@tanstack/react-query";
import { TableCard } from "ui-components/Layout";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { ReturnOrderScanColumns } from "./columns";
import { useState } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useTeamValue } from "providers/TeamContext";
import dayjs from "dayjs";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import { Grid } from "@mui/material";
import { LoadingDialog } from "ui-components/Alert";
import { getReturnOrderScanEnquiry } from "services/data.report.service";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import { ExportDialog } from "ui-components/Alert";
import { SelectFields } from "ui-components/Form";
import { getCustomerOptions } from "services/data.internal.service";

function ReturnOrderScanEnquiry() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode, customerOpt } = useCustomerValue();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const exportPermission =
    allowAll ||
    (Boolean(permissions["Return Order_Report"]) &&
      permissions["Return Order_Report"].findIndex((b) => b === "export") !== -1);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const sevenDaysBefore = dayjs(new Date()).subtract(1, "day").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(sevenDaysBefore);
  const [endDate, setEndDate] = useState(date);
  const customProps = { startDate, setStartDate, endDate, setEndDate };
  const [alert, setAlert] = useState(null);
  const { isDownloading, downloadCsv } = useCsvDownloadHook();
  const [customers, setCustomers] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);

  useQuery({
    queryKey: ["customer-options", { customerCode }],
    queryFn: () => getCustomerOptions(),
    onSuccess: (data) => {
      const activeCust = customerOpt.filter(x => data.data.some(y => y === x));
      setCustomerOptions(activeCust);
      setCustomers([customerCode]);
    },
    onError: () => {},
  });

  const onFetch = () => {
    setAlert(null);
    const valid =
      dayjs(endDate).isSame(dayjs(startDate)) || dayjs(endDate).isAfter(dayjs(startDate));
    if (!valid) {
      setAlert(<Error_ content="Start Date cannot greater than End Date" onChange={setAlert} />);
      return;
    }
    if (dayjs(date).isBefore(endDate)) {
      setAlert(<Error_ content="End Date cannot greater than Today Date" onChange={setAlert} />);
      return;
    }
    refetch();
  };
  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  const { refetch, isFetching } = useQuery({
    queryKey: ["return-order-scan-enquiry", { customerCode, customers }],
    queryFn: () => getReturnOrderScanEnquiry({ customerCode, customers, startDate, endDate }),
    enabled: Boolean(customerCode) && Boolean(customers.length),
    onSuccess: (data) => {
      setData(data.data.data);
      setFilteredData(data.data.data);
    },
    onError: () => {},
  });
  return (
    <ListLayout>
      {isFetching && <LoadingDialog />}
      {isDownloading && <ExportDialog />}
      {alert && <MDBox sx={{ my: 3 }}>{alert}</MDBox>}
      <TableCard sx={{ my: 3 }}>
        <MDBox margin="1rem 0 0 1rem">
          <Grid container spacing={3}>
            <Grid item xs={3}>
              {(Boolean(customerOptions) && Boolean(customerOptions.length)) && (
                <SelectFields
                  field={{
                    name: "",
                    label: "Customers",
                    value: customers,
                    errors: {},
                    multiple: true,
                  }}
                  options={customerOptions}
                  onChange={(e, v) => setCustomers(v)}
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <CustomDateRangePicker {...customProps} />
            </Grid>
            <Grid item xs={5}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  onFetch();
                }}
              >
                Search
              </MDButton>
              <MDButton
                sx={{ marginLeft: "1rem" }}
                variant="gradient"
                color="info"
                disabled={!exportPermission}
                onClick={() => {
                  const filename = `${customers.join(', ')}_${dayjs(new Date()).format(
                    "YYYYMMDD-HHmm"
                  )}_ReturnOrder`;
                  downloadCsv(
                    filename,
                    filteredData.map((x) => {
                      const { ...d } = x;
                      return {
                        ...d,
                        custOrdNbr: "'" + x.custOrdNbr,
                        trackingNumber: "'" + x.trackingNumber,
                      };
                    })
                  );
                }}
              >
                Export
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
        <BasicTable
          data={data}
          columns={ReturnOrderScanColumns}
          onFilterRowsChange={handleFilterRowsChange}
        />
      </TableCard>
    </ListLayout>
  );
}

export default ReturnOrderScanEnquiry;